import React, { useEffect, useRef, useState } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import { Box } from '@chakra-ui/react';
import { registerQuillEmailFormats, SIZE_WHITELIST, FONT_WHITELIST } from '../../pages/TemplateManager/CreateTemplate/EmailDesigner/quillEmailSetup';
import { normalizeQuillHtml } from '../../pages/TemplateManager/CreateTemplate/EmailDesigner/htmlExport';

// Switch Quill size/font to inline styles with real whitelists (idempotent, global).
registerQuillEmailFormats();

const TOOLBAR_OPTIONS = [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    [{ font: FONT_WHITELIST }],
    [{ size: SIZE_WHITELIST }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ script: 'sub' }, { script: 'super' }],
    [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
    [{ indent: '-1' }, { indent: '+1' }],
    [{ direction: 'rtl' }],
    [{ align: [] }],
    ['link', 'image', 'video', 'blockquote', 'code-block'],
    ['clean'],
];

const isFullHtmlDoc = (html) => {
    const trimmed = (html || '').trim().toLowerCase();
    return trimmed.startsWith('<!doctype') || trimmed.startsWith('<html');
};

const extractBody = (html) => {
    if (!isFullHtmlDoc(html)) return html;
    const match = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    return match ? match[1] : html;
};

const RichTextEditor = ({ value, onChange, placeholder = 'Write email content here...', toolbar, stickyToolbar = false, minHeight = 220, autoFocus = false, onInsertHandleReady }) => {
    const editorRef = useRef(null);
    const quillRef = useRef(null);
    const isInternalChange = useRef(false);
    // Last caret position seen while the editor had focus. Clicking a button OUTSIDE
    // the editor (e.g. the sidebar's insert-variable) blurs it and Quill's
    // getSelection() then returns null, so the range has to be remembered here or
    // there is nothing to insert *at* (ET_007).
    const lastRangeRef = useRef(null);
    const [showSource, setShowSource] = useState(() => isFullHtmlDoc(value));

    useEffect(() => {
        if (showSource) return;
        if (editorRef.current && !quillRef.current) {
            quillRef.current = new Quill(editorRef.current, {
                theme: 'snow',
                placeholder,
                modules: { toolbar: toolbar || TOOLBAR_OPTIONS },
            });

            quillRef.current.on('selection-change', (range) => {
                if (range) lastRangeRef.current = range;
            });

            if (onInsertHandleReady) {
                onInsertHandleReady((text) => {
                    const q = quillRef.current;
                    if (!q) return;
                    // No caret yet (never focused) → append, matching the old behaviour.
                    // getLength() counts Quill's trailing newline, hence the -1.
                    const r = lastRangeRef.current || { index: Math.max(0, q.getLength() - 1), length: 0 };
                    const at = Math.min(r.index, Math.max(0, q.getLength() - 1));
                    if (r.length) q.deleteText(at, r.length, 'user');
                    q.insertText(at, text, 'user');
                    q.setSelection(at + text.length, 0);
                    lastRangeRef.current = { index: at + text.length, length: 0 };
                });
            }

            quillRef.current.on('text-change', () => {
                isInternalChange.current = true;
                // normalizeQuillHtml: getSemanticHTML() turns EVERY space into
                // &nbsp;, which makes the paragraph unwrappable downstream.
                if (onChange) onChange(normalizeQuillHtml(quillRef.current.getSemanticHTML()));
                isInternalChange.current = false;
            });

            // Put the cursor in the editor immediately so the user can just type.
            if (autoFocus) {
                const q = quillRef.current;
                setTimeout(() => { q.focus(); q.setSelection(q.getLength(), 0); }, 0);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showSource]);

    useEffect(() => {
        if (showSource) return;
        const quill = quillRef.current;
        if (!quill || isInternalChange.current) return;

        const htmlToSet = isFullHtmlDoc(value) ? extractBody(value) : (value || '');
        // Compare like for like. `value` has been through normalizeQuillHtml on the
        // way out, getSemanticHTML() has not — without normalising both sides every
        // keystroke would look like an external change and re-paste the document,
        // throwing the caret back to the start.
        const currentHtml = normalizeQuillHtml(quill.getSemanticHTML());
        if (normalizeQuillHtml(htmlToSet) !== currentHtml) {
            const selection = quill.getSelection();
            quill.clipboard.dangerouslyPasteHTML(htmlToSet);
            if (selection) {
                const length = quill.getLength();
                quill.setSelection(Math.min(selection.index, length - 1), selection.length);
            }
        }
    }, [value, showSource]);

    const handleToggleSource = () => {
        if (showSource) quillRef.current = null;
        setShowSource(prev => !prev);
    };

    return (
        <Box>
            {/* <HStack justify="flex-end" mb={2}>
                <Tooltip label={showSource ? 'Switch to rich text editor' : 'Edit raw HTML code'}>
                    <Button
                        size="xs"
                        variant="outline"
                        leftIcon={showSource ? <FiEdit2 size={12} /> : <FiCode size={12} />}
                        onClick={handleToggleSource}
                    >
                        {showSource ? 'Rich Text' : 'HTML Code'}
                    </Button>
                </Tooltip>
            </HStack> */}

            {showSource ? (
                <textarea
                    value={value || ''}
                    onChange={(e) => onChange && onChange(e.target.value)}
                    placeholder="Paste your full HTML email code here..."
                    style={{
                        width: '100%',
                        minHeight: '320px',
                        fontFamily: '"Courier New", Courier, monospace',
                        fontSize: '12px',
                        padding: '12px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        resize: 'vertical',
                        outline: 'none',
                        lineHeight: '1.5',
                        backgroundColor: '#f8fafc',
                        color: '#1a202c',
                        boxSizing: 'border-box',
                    }}
                />
            ) : (
                <Box
                    sx={{
                        '.ql-container': {
                            minHeight: `${minHeight}px`,
                            fontSize: '14px',
                            borderBottomLeftRadius: '6px',
                            borderBottomRightRadius: '6px',
                        },
                        '.ql-editor': {
                            minHeight: `${minHeight}px`,
                        },
                        '.ql-toolbar': {
                            borderTopLeftRadius: '6px',
                            borderTopRightRadius: '6px',
                            // Pinned bar-on-top: stays visible while the content scrolls.
                            ...(stickyToolbar ? {
                                position: 'sticky',
                                top: 0,
                                zIndex: 2,
                                background: '#fff',
                                boxShadow: '0 1px 0 rgba(0,0,0,0.04)',
                            } : {}),
                        },
                        // Tighten the toolbar into a clean single bar in narrow panels.
                        '.ql-toolbar.ql-snow': { padding: '6px 8px', border: '1px solid #e2e8f0' },
                        '.ql-toolbar.ql-snow .ql-formats': { marginRight: '8px' },
                        '.ql-snow .ql-picker.ql-font': { width: '92px' },
                        '.ql-snow .ql-picker.ql-size': { width: '64px' },
                    }}
                >
                    <div ref={editorRef} />
                </Box>
            )}
        </Box>
    );
};

export default RichTextEditor;